import type { LobbyRoomView } from "@/shared/room-state"
import type { TopicCategory } from "@/shared/game/types"

type RoomSocketStatus = "idle" | "connecting" | "authed" | "disconnected" | "reconnecting" | "expired"
type RoomSocketEvent = "status" | "state" | "error" | "authed" | "expired" | "kicked" | "left"
type RoomSocketHandler = (payload: unknown) => void

interface StoredSession {
  roomCode: string
  playerId: string
  sessionToken: string
}

const SESSION_STORAGE_KEY = "bibleImposter.session"
const RECONNECT_DELAYS_SECONDS = [1, 2, 4, 8, 16, 32]
const RECONNECT_JITTER = 0.2
const HEARTBEAT_INTERVAL_MS = 25_000
const HEARTBEAT_TIMEOUT_MS = 5_000

export type { LobbyRoomView, RoomSocketStatus, StoredSession, TopicCategory }
export { SESSION_STORAGE_KEY }

export function reconnectDelayForAttempt(attempt: number, random = Math.random()) {
  const clamped = Math.min(Math.max(attempt, 0), RECONNECT_DELAYS_SECONDS.length - 1)
  const baseMs = RECONNECT_DELAYS_SECONDS[clamped] * 1000
  const jitter = baseMs * RECONNECT_JITTER * (random * 2 - 1)
  return Math.max(0, baseMs + jitter)
}

export function readStoredSession(roomCode?: string) {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY)

  if (!raw) {
    return null
  }

  const parsed = JSON.parse(raw) as StoredSession
  return !roomCode || parsed.roomCode === roomCode ? parsed : null
}

export function writeStoredSession(session: StoredSession) {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
}

export class RoomSocket {
  private socket: WebSocket | null = null
  private pendingFirstMessage: string | null = null
  private handlers = new Map<RoomSocketEvent, Set<RoomSocketHandler>>()
  private reconnectAttempt = 0
  private manuallyClosed = false
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null
  private heartbeatTimeout: ReturnType<typeof setTimeout> | null = null
  private receivedServerMessage = false
  status: RoomSocketStatus = "idle"
  private readonly roomCode: string

  constructor(roomCode: string) {
    this.roomCode = roomCode
  }

  connect() {
    this.manuallyClosed = false
    this.receivedServerMessage = false
    this.setStatus(this.reconnectAttempt > 0 ? "reconnecting" : "connecting")

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
    const socket = new WebSocket(`${protocol}//${window.location.host}/ws/room/${this.roomCode}`)
    this.socket = socket

    socket.addEventListener("open", () => {
      const session = this.readSession()

      if (session) {
        socket.send(
          JSON.stringify({
            type: "AUTH",
            playerId: session.playerId,
            sessionToken: session.sessionToken,
          })
        )
      } else if (this.pendingFirstMessage) {
        socket.send(this.pendingFirstMessage)
        this.pendingFirstMessage = null
      }
    })

    socket.addEventListener("message", (event) => this.handleMessage(String(event.data)))
    socket.addEventListener("close", () => {
      if (this.socket !== socket) {
        return
      }

      this.socket = null
      this.clearHeartbeat()

      if (!this.manuallyClosed && !this.receivedServerMessage) {
        void this.handleNoMessageClose()
        return
      }

      if (!this.manuallyClosed) {
        this.setStatus("disconnected")
        this.scheduleReconnect()
      }
    })
  }

  join(name: string, deviceToken: string) {
    this.sendOrQueueFirstMessage(JSON.stringify({ type: "JOIN", name, deviceToken }))
  }

  spectate(name: string, deviceToken: string) {
    this.sendOrQueueFirstMessage(JSON.stringify({ type: "SPECTATE", name, deviceToken }))
  }

  startRound() {
    this.sendAuthenticatedAction({ type: "START_ROUND" })
  }

  cancelCountdown() {
    this.sendAuthenticatedAction({ type: "CANCEL_COUNTDOWN" })
  }

  beginDiscussion() {
    this.sendAuthenticatedAction({ type: "BEGIN_DISCUSSION" })
  }

  openVote() {
    this.sendAuthenticatedAction({ type: "OPEN_VOTE" })
  }

  submitVote(suspectId: string) {
    this.sendAuthenticatedAction({ type: "SUBMIT_VOTE", suspectId })
  }

  kickPlayer(targetId: string) {
    this.sendAuthenticatedAction({ type: "KICK_PLAYER", targetId })
  }

  updateCategoryFilter(categories: TopicCategory[]) {
    this.sendAuthenticatedAction({ type: "UPDATE_CATEGORY_FILTER", categories })
  }

  leaveRoom() {
    // Best-effort tell the server we're leaving. If we're authed and the
    // socket is open the server will receive LEAVE_ROOM, drop us from the
    // round, and close the connection cleanly. If we're not (mid-reconnect,
    // offline, never connected, etc.) we still tear down locally so the
    // player isn't trapped on /r/CODE with a stale session; the server
    // will GC the player on its next disconnect timeout.
    if (this.socket?.readyState === WebSocket.OPEN && this.status === "authed") {
      const session = this.readSession()
      if (session) {
        this.socket.send(
          JSON.stringify({
            type: "LEAVE_ROOM",
            playerId: session.playerId,
            sessionToken: session.sessionToken,
          })
        )
      }
    }

    // Mark the close as intentional BEFORE we trigger it so the close
    // handler doesn't fall through to scheduleReconnect().
    this.manuallyClosed = true
    this.clearHeartbeat()
    this.socket?.close()

    // Wipe session AFTER marking the socket as manually closed so any
    // racing close handler that re-reads localStorage sees a clean slate.
    localStorage.removeItem(SESSION_STORAGE_KEY)
    this.setStatus("idle")
    this.emit("left", null)
  }

  close() {
    this.manuallyClosed = true
    this.clearHeartbeat()
    this.socket?.close()
  }

  on(event: RoomSocketEvent, handler: RoomSocketHandler) {
    const handlers = this.handlers.get(event) ?? new Set<RoomSocketHandler>()
    handlers.add(handler)
    this.handlers.set(event, handlers)

    return () => handlers.delete(handler)
  }

  private handleMessage(raw: string) {
    this.receivedServerMessage = true

    if (raw === "pong") {
      this.clearHeartbeatTimeout()
      return
    }

    const message = JSON.parse(raw) as {
      type: string
      payload?: unknown
      playerId?: string
      sessionToken?: string
      spectatorToken?: string
      error?: string
      reason?: string
    }

    if (message.type === "KICKED") {
      localStorage.removeItem(SESSION_STORAGE_KEY)
      this.manuallyClosed = true
      this.clearHeartbeat()
      this.socket?.close()
      this.emit("kicked", message.reason ?? "KICKED")
      return
    }

    if (message.type === "AUTH_OK") {
      this.reconnectAttempt = 0
      this.setStatus("authed")
      this.startHeartbeat()
      this.emit("authed", null)
      return
    }

    if (message.type === "SPECTATE_OK" && message.playerId && message.spectatorToken) {
      this.reconnectAttempt = 0
      this.setStatus("authed")
      this.startHeartbeat()
      this.emit("authed", null)
      return
    }

    if (message.type === "JOIN_OK" && message.playerId && message.sessionToken) {
      localStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({
          roomCode: this.roomCode,
          playerId: message.playerId,
          sessionToken: message.sessionToken,
        } satisfies StoredSession)
      )
      this.reconnectAttempt = 0
      this.setStatus("authed")
      this.startHeartbeat()
      this.emit("authed", null)
      return
    }

    if (message.type === "ROOM_EXPIRED") {
      this.expireSession("ROOM_EXPIRED")
      return
    }

    if (message.type === "STATE") {
      this.emit("state", message.payload)
      return
    }

    if (message.type === "AUTH_FAILED" || message.type === "ACTION_REJECTED") {
      if (
        message.type === "AUTH_FAILED" &&
        (message.reason === "UNKNOWN_PLAYER" || message.reason === "BAD_TOKEN")
      ) {
        this.expireSession(message.reason)
      }
      this.emit("error", message)
    }
  }

  private scheduleReconnect() {
    const delay = reconnectDelayForAttempt(this.reconnectAttempt)
    this.reconnectAttempt += 1
    window.setTimeout(() => this.connect(), delay)
  }

  private async handleNoMessageClose() {
    const session = this.readSession()

    if (!session) {
      this.manuallyClosed = true
      this.setStatus("idle")
      this.emit("error", { reason: "ROOM_NOT_FOUND" })
      return
    }

    const roomExists = await this.roomExists()

    if (roomExists === false) {
      this.expireSession("UNKNOWN_PLAYER")
      return
    }

    this.setStatus("disconnected")
    this.scheduleReconnect()
  }

  private async roomExists() {
    try {
      const response = await fetch(`/api/rooms/${this.roomCode}/exists`, { cache: "no-store" })
      if (!response.ok) {
        return null
      }

      const payload = (await response.json()) as { exists?: boolean }
      return payload.exists === true
    } catch {
      return null
    }
  }

  private readSession() {
    return readStoredSession(this.roomCode)
  }

  private sendOrQueueFirstMessage(message: string) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(message)
      return
    }

    this.pendingFirstMessage = message

    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      this.connect()
    }
  }

  private sendAuthenticatedAction(message: Record<string, unknown>) {
    const session = this.readSession()

    if (!session) {
      this.emit("error", { reason: "NO_SESSION" })
      return
    }

    if (this.socket?.readyState !== WebSocket.OPEN || this.status !== "authed") {
      return
    }

    this.socket.send(
      JSON.stringify({
        ...message,
        playerId: session.playerId,
        sessionToken: session.sessionToken,
      })
    )
  }

  private startHeartbeat() {
    this.clearHeartbeat()
    this.heartbeatInterval = window.setInterval(() => {
      if (this.socket?.readyState !== WebSocket.OPEN) {
        return
      }

      this.socket.send("ping")
      this.clearHeartbeatTimeout()
      this.heartbeatTimeout = window.setTimeout(() => {
        this.socket?.close()
      }, HEARTBEAT_TIMEOUT_MS)
    }, HEARTBEAT_INTERVAL_MS)
  }

  private clearHeartbeat() {
    if (this.heartbeatInterval) {
      window.clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
    this.clearHeartbeatTimeout()
  }

  private clearHeartbeatTimeout() {
    if (this.heartbeatTimeout) {
      window.clearTimeout(this.heartbeatTimeout)
      this.heartbeatTimeout = null
    }
  }

  private expireSession(reason: string) {
    localStorage.removeItem(SESSION_STORAGE_KEY)
    this.manuallyClosed = true
    this.clearHeartbeat()
    this.socket?.close()
    this.setStatus("expired")
    this.emit("expired", reason)
  }

  private setStatus(status: RoomSocketStatus) {
    this.status = status
    this.emit("status", status)
  }

  private emit(event: RoomSocketEvent, payload: unknown) {
    for (const handler of this.handlers.get(event) ?? []) {
      handler(payload)
    }
  }
}
